import { Link } from "react-router-dom";
import Reveal from "@/components/feature/Reveal";
import SlimZeroLpLink from "@/components/feature/SlimZeroLpLink";
import { ReserveButton } from "@/components/layout/ReserveButton";
import { LineButton } from "@/components/layout/LineButton";
import { clinic } from "@/mocks/clinic";
import { FeatureList, TreatmentIndexNav, TreatmentSection } from "@/pages/treatment/components/TreatmentSections";
import { DowntimeTable, FlowSteps, MethodTable } from "@/pages/treatment/components/TreatmentProcess";
import { TreatmentFaq } from "@/pages/treatment/components/TreatmentFaqCta";
import TreatmentRelatedLinks from "@/pages/treatment/components/TreatmentRelatedLinks";
import SlimZeroPriceTable from "@/pages/treatment/components/SlimZeroPriceTable";
import SlimInjectionHero from "./SlimInjectionHero";
import { isPendingValue } from "@/utils/isPendingContent";

/* NOTE: 薬剤名・成分名・製品名・配合量・濃度・作用機序・確定していない効果/料金は一切掲載しない。
   未確認の医療情報は推測して作成しない。 */

const indexItems = [
  { label: "この施術について", target: "sli-about" },
  { label: "このようなお悩みの方へ", target: "sli-concerns" },
  { label: "Medical Slim ZEROでの位置づけ", target: "sli-position" },
  { label: "気になる部位", target: "sli-areas" },
  { label: "特徴", target: "sli-features" },
  { label: "施術情報", target: "sli-method" },
  { label: "施術の流れ", target: "sli-flow" },
  { label: "ダウンタイム", target: "sli-downtime" },
  { label: "リスク・注意事項", target: "sli-risk" },
  { label: "料金", target: "sli-price" },
  { label: "Q&A", target: "sli-faq" },
];

const concerns = [
  "お腹まわりが気になる",
  "二の腕が気になる",
  "太ももなど特定の部位について相談したい",
  "部分的なボディラインが気になる",
  "運動や食事管理だけでは気になる部分が残る",
  "外科的な痩身治療以外の選択肢も検討したい",
  "自分に合った医療痩身を相談したい",
];

const slimTreatments = [
  { name: "新脂肪溶解注射", href: "/slim/injection" },
  { name: "RF（ラジオ波）", href: "/slim/rf" },
  { name: "脂肪冷却", href: "/slim/cooling" },
  { name: "医療用EMS", href: "/slim/ems" },
];

const areas = ["お腹", "二の腕", "太もも", "腰まわり", "その他の気になる部位"];

const methodItems = [
  { label: "分類", value: "MEDICAL SLIM ZERO" },
  { label: "施術方法", value: "注入治療" },
];

const features = [
  {
    no: "01",
    title: "気になる部位について相談できる",
    text: "全身のダイエットとは別に、特定の部位について相談したい方の選択肢となる医療痩身治療です。",
  },
  {
    no: "02",
    title: "医師の診察のもと治療",
    text: "体型や施術部位、身体の状態などを確認し、医師が治療の適応を判断します。",
  },
  {
    no: "03",
    title: "Medical Slim ZEROの他治療も相談可能",
    text: "RF・脂肪冷却・医療用EMSなど、他の医療痩身治療についてもご相談いただけます。",
  },
  {
    no: "04",
    title: "一人ひとりに合わせた治療提案",
    text: "ご希望だけではなく、施術部位や身体の状態も確認したうえで治療内容をご案内します。",
  },
];

const flowSteps = [
  { no: "01", title: "ご予約", text: "WEB・LINE・電話からご予約ください。" },
  { no: "02", title: "カウンセリング", text: "体型や気になる部位、ご希望などを伺います。" },
  { no: "03", title: "医師の診察", text: "身体の状態や施術部位を確認し、治療の適応を判断します。" },
  { no: "04", title: "治療内容・料金のご説明", text: "施術内容、施術範囲、料金、リスクなどをご説明します。" },
  { no: "05", title: "施術", text: "治療内容に同意いただいたうえで施術を行います。" },
  { no: "06", title: "アフターケア", text: "施術後の過ごし方や注意事項をご案内します。" },
];

const specItems = [
  { label: "施術時間", value: "—" },
  { label: "麻酔", value: "—" },
  { label: "ダウンタイム", value: "—" },
  { label: "推奨回数", value: "—" },
  { label: "推奨間隔", value: "—" },
];

const riskParagraphs = [
  "注射による治療のため、施術部位に腫れ、赤み、痛み、内出血などが生じる場合があります。",
  "使用する製剤に関するリスク・副作用・禁忌については、正式情報を確認のうえ掲載します。",
  "治療を受けられない場合や注意事項については、診察時に医師よりご説明します。",
  "症状や経過には個人差があります。",
];

const faqItems = [
  {
    q: "どの部位に施術できますか？",
    a: "施術可能な部位は身体の状態や治療内容によって異なります。気になる部位についてカウンセリング・診察時にご相談ください。",
  },
  {
    q: "何回くらい受ける必要がありますか？",
    a: "必要な治療回数は施術部位や身体の状態、ご希望などによって異なります。診察時に治療プランをご案内します。",
  },
  {
    q: "他のMedical Slim ZEROの治療と組み合わせられますか？",
    a: "RF（ラジオ波）、脂肪冷却、医療用EMSなど、他の医療痩身治療についてもご相談いただけます。治療内容は診察のうえご案内します。",
  },
  {
    q: "ダイエット中でも相談できますか？",
    a: "体重管理や生活習慣についても確認しながら、気になる部位に対する医療痩身の選択肢をご案内します。",
  },
  {
    q: "当日に施術できますか？",
    a: "診察結果や予約状況などにより異なります。詳しくはご予約時にお問い合わせください。",
  },
];

const relatedItems = [
  { label: "Medical Slim ZEROへ戻る", to: "/treatment/slim-zero" },
  { label: "RF（ラジオ波）を見る", to: "/slim/rf" },
  { label: "脂肪冷却を見る", to: "/slim/cooling" },
  { label: "医療用EMSを見る", to: "/slim/ems" },
];

export default function SlimInjectionDetail() {
  // 施術時間・麻酔・ダウンタイム等がすべて未確定の場合はセクションごと非表示にする
  const specRows = specItems.filter((item) => !isPendingValue(item.value));
  const visibleIndexItems = indexItems.filter(
    (item) => !(item.target === "sli-downtime" && specRows.length === 0),
  );

  return (
    <>
      <SlimInjectionHero />
      <TreatmentIndexNav items={visibleIndexItems} />

      {/* 01 ABOUT */}
      <TreatmentSection id="sli-about" en="01 / ABOUT TREATMENT" jp="新脂肪溶解注射とは" tone="plain">
        <Reveal>
          <div className="max-w-3xl space-y-6 text-[14px] leading-[2.3] text-foreground-700 md:text-[15px]">
            <p>新脂肪溶解注射は、気になる部位に対して行う医療痩身治療の一つです。</p>
            <p>全身の体重管理とは異なり、特定の部位について相談したい方の選択肢としてご案内しています。</p>
            <p>
              SBDCでは、一人ひとりの体型や気になる部位、ご希望を確認したうえで、医師の診察のもと治療内容をご提案します。
            </p>
            <p>施術の適応、施術部位、使用量、治療回数などについては、診察内容に応じて判断します。</p>
          </div>
        </Reveal>
      </TreatmentSection>

      {/* 02 RECOMMENDED FOR */}
      <TreatmentSection id="sli-concerns" en="02 / RECOMMENDED FOR" jp="このようなお悩みの方へ" tone="tint">
        <Reveal>
          <p className="max-w-2xl text-[13px] leading-[2.1] text-foreground-700 md:text-sm">
            このようなお悩みについてご相談いただけます。
          </p>
          <ul className="mt-8 grid grid-cols-1 gap-px border border-foreground-950/12 bg-foreground-950/12 sm:grid-cols-2">
            {concerns.map((concern, index) => (
              <li key={concern} className="flex items-start gap-4 bg-background-50 px-6 py-6">
                <span className="pt-0.5 font-label text-[11px] tracking-[0.2em] text-secondary-500">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="font-heading text-[15px] leading-[1.8] text-foreground-900 md:text-base">
                  {concern}
                </span>
              </li>
            ))}
          </ul>
        </Reveal>
      </TreatmentSection>

      {/* 03 MEDICAL SLIM ZERO */}
      <TreatmentSection
        id="sli-position"
        en="03 / MEDICAL SLIM ZERO"
        jp="一つの治療だけに頼らない医療痩身"
        tone="plain"
      >
        <Reveal>
          <div className="max-w-3xl space-y-6 text-[14px] leading-[2.3] text-foreground-700 md:text-[15px]">
            <p>
              Medical Slim ZEROでは、すべての方に同じ治療を行うのではなく、体型・部位・お悩み・ご希望を確認しながら治療方法を検討します。
            </p>
            <p>新脂肪溶解注射のほか、</p>
            <ul className="space-y-2.5">
              {["RF（ラジオ波）", "脂肪冷却", "医療用EMS"].map((item) => (
                <li key={item} className="flex items-center gap-3 text-foreground-800">
                  <span className="h-px w-5 bg-accent-500" />
                  {item}
                </li>
              ))}
            </ul>
            <p>などの治療もご用意しています。</p>
            <p>気になる部位や目的に応じて、単独治療または複数の治療についてご相談いただけます。</p>
          </div>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-px border border-foreground-950/12 bg-foreground-950/12 sm:grid-cols-2 md:mt-12">
          {slimTreatments.map((treatment, index) => (
            <Link
              key={treatment.href}
              to={treatment.href}
              className="group flex items-center justify-between gap-4 bg-background-50 px-7 py-7 transition-colors hover:bg-secondary-100/50"
            >
              <span className="flex items-baseline gap-4">
                <span className="font-label text-[12px] tracking-[0.25em] text-secondary-600">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="font-heading text-[16px] text-foreground-950 md:text-lg">{treatment.name}</span>
              </span>
              <i
                className="ri-arrow-right-line text-lg text-foreground-400 transition-all group-hover:translate-x-1 group-hover:text-accent-700"
                aria-hidden
              />
            </Link>
          ))}
        </div>
      </TreatmentSection>

      {/* 04 TREATMENT AREAS */}
      <TreatmentSection id="sli-areas" en="04 / TREATMENT AREAS" jp="気になる部位を相談できます" tone="tint">
        <Reveal>
          <ul className="grid grid-cols-1 gap-px border border-foreground-950/12 bg-foreground-950/12 sm:grid-cols-2 lg:grid-cols-3">
            {areas.map((area, index) => (
              <li key={area} className="flex items-start gap-4 bg-background-50 px-6 py-7">
                <span className="pt-0.5 font-label text-[11px] tracking-[0.2em] text-secondary-500">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="font-heading text-[15px] leading-[1.8] text-foreground-900 md:text-base">
                  {area}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-8 max-w-3xl border-l-2 border-accent-500 pl-4 text-[12px] leading-[2] text-foreground-600">
            施術可能な部位については、身体の状態や診察内容によって異なります。詳しくは診察時にご相談ください。
          </p>
        </Reveal>
      </TreatmentSection>

      {/* 05 FEATURES */}
      <TreatmentSection id="sli-features" en="05 / FEATURES" jp="新脂肪溶解注射の特徴" tone="plain">
        <FeatureList items={features} />
      </TreatmentSection>

      {/* 06 METHOD */}
      <TreatmentSection id="sli-method" en="06 / METHOD" jp="施術情報" tone="tint">
        <Reveal>
          <MethodTable items={methodItems} />
        </Reveal>
      </TreatmentSection>

      {/* 07 FLOW */}
      <TreatmentSection id="sli-flow" en="07 / TREATMENT FLOW" jp="施術の流れ" tone="plain">
        <FlowSteps items={flowSteps} />
      </TreatmentSection>

      {/* 08 DOWNTIME（未確定項目は非表示。全項目が未確定の場合はセクションごと非表示） */}
      {specRows.length > 0 ? (
        <TreatmentSection id="sli-downtime" en="08 / DOWNTIME" jp="施術時間・ダウンタイム" tone="tint">
          <DowntimeTable items={specRows} />
        </TreatmentSection>
      ) : null}

      {/* 09 RISK / PRECAUTIONS */}
      <TreatmentSection id="sli-risk" en="09 / RISK / PRECAUTIONS" jp="リスク・注意事項" tone="plain">
        <Reveal>
          <div className="max-w-3xl border-l-2 border-accent-500 pl-5 md:pl-6">
            <div className="space-y-6 text-[14px] leading-[2.3] text-foreground-700 md:text-[15px]">
              {riskParagraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>
        </Reveal>
      </TreatmentSection>

      {/* 10 PRICE */}
      <TreatmentSection id="sli-price" en="10 / PRICE" jp="料金" tone="tint">
        <div className="space-y-10">
          <SlimZeroPriceTable onlySlug="injection" />
          <SlimZeroLpLink
            note="プラン・詳細はMedical Slim ZERO専用ページでご確認いただけます。"
            label="Medical Slim ZEROのプラン・詳細を見る"
          />
        </div>
      </TreatmentSection>

      {/* 11 FAQ */}
      <TreatmentSection id="sli-faq" en="11 / Q&amp;A" jp="よくあるご質問" tone="plain">
        <div className="max-w-3xl">
          <TreatmentFaq items={faqItems} />
        </div>
      </TreatmentSection>

      {/* RELATED LINKS */}
      <TreatmentRelatedLinks items={relatedItems} />

      {/* FINAL CTA */}
      <section className="relative overflow-hidden bg-primary-700 py-20 md:py-24 lg:py-28">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-20 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full border border-background-50/10"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-40 top-1/2 h-[420px] w-[420px] -translate-y-1/2 rounded-full border border-background-50/10"
        />
        <div className="u-container relative">
          <Reveal>
            <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
              <span className="en-eyebrow text-[10px] text-accent-300 md:text-[11px]">CONTACT / RESERVATION</span>
              <h2 className="mt-7 font-heading text-[24px] leading-[1.7] text-background-50 md:text-[34px]">
                気になる部分から、
                <br />
                まずはご相談ください。
              </h2>
              <p className="mt-7 max-w-xl text-[13px] leading-[2.2] text-background-200 md:text-sm">
                どの医療痩身が自分に合っているか分からない方もご相談いただけます。体型やお悩みを伺い、一人ひとりに合わせて治療をご提案します。
              </p>

              <div className="mt-10 flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center">
                <LineButton className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-8 py-3.5 text-[14px] font-medium text-primary-700 transition-colors hover:bg-secondary-100">
                  <i className="ri-line-fill text-base" aria-hidden />
                  新脂肪溶解注射についてLINEで相談
                </LineButton>
                <ReserveButton className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-8 py-3.5 text-[14px] text-background-50 transition-colors hover:bg-background-50/10">
                  <i className="ri-calendar-check-line text-base" aria-hidden />
                  無料カウンセリングを予約
                </ReserveButton>
              </div>

              <p className="mt-8 text-[12px] tracking-wide text-background-200/80">
                {clinic.hours} ／ 渋谷駅近く
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}